<script setup lang="ts">
import { RangeCalendarCell, RangeCalendarCellTrigger, RangeCalendarGrid, RangeCalendarGridBody, RangeCalendarGridHead, RangeCalendarGridRow, RangeCalendarHeadCell, RangeCalendarHeader, RangeCalendarHeading, RangeCalendarNext, RangeCalendarPrev, RangeCalendarRoot, useDateFormatter } from 'radix-vue'
import type { DateRange } from 'radix-vue'
import type { DateValue } from '@internationalized/date'
import { parseDate, parseDateTime, toCalendarDate } from '@internationalized/date'
import type { YscDateRange } from '../types'
import { areDateRangeEquals } from '../utils/areDateRangeEquals'
import { useNuxtApp, ref, watch, computed } from '#imports'

const { $i18n: { locale } } = useNuxtApp()

export interface Props {
  modelValue?: YscDateRange
  numberOfMonths?: 1 | 2
  minDate?: string
  maxDate?: string
  dateDisabled?: YscDateRange[]
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  numberOfMonths: 1,
  minDate: undefined,
  maxDate: undefined,
  dateDisabled: undefined,
})

const emits = defineEmits<{
  'update:model-value': [YscDateRange | undefined]
}>()

const localValue = ref<DateRange | undefined>()

watch(() => props.modelValue, (val) => {
  if (!val || !val.start || !val.end) {
    localValue.value = {
      start: undefined,
      end: undefined,
    }
    return
  }

  const parsedDate = {
    start: toCalendarDate(parseDateTime(val.start)),
    end: toCalendarDate(parseDateTime(val.end)),
  }

  if (!localValue.value || !areDateRangeEquals(localValue.value, parsedDate)) {
    localValue.value = parsedDate
  }
}, { immediate: true })

watch(localValue, (val) => {
  if (!val || !val.start || !val.end) {
    return
  }
  emits('update:model-value', {
    start: val?.start?.toString(),
    end: val?.end?.toString(),
  })
})
const minValue = computed(() => props.minDate ? parseDate(props.minDate) : undefined)
const maxValue = computed(() => props.maxDate ? parseDate(props.maxDate) : undefined)

const isDateUnavailable = (date: DateValue) => {
  if (!props.dateDisabled) {
    return undefined
  }
  return props.dateDisabled.some((interval) => {
    if (!interval.start || !interval.end) {
      return false
    }
    const intervalStart = toCalendarDate(parseDateTime(interval.start))
    const intervalEnd = toCalendarDate(parseDateTime(interval.end))
    return date.compare(intervalStart) >= 0 && date.compare(intervalEnd) <= 0
  })
}

const formatter = useDateFormatter(locale.value)

const weeksStartsOn = computed(() => {
  // When using PT language the underlying date library used by radix make the week starts on sunday.
  // Per request (TT-9521) we override it so it starts on monday.
  if (locale.value === 'pt-pt') {
    return 1
  }
  // Keep using default value for other languages.
  return undefined
})

const localeFixed = computed(() => {
  if (locale.value === 'pt-pt') {
    // 'pt-pt' is not handled properly by the formatting library, the day name are not formatted in the "narrow" format causing display issue.
    // so we set it as 'pt' which is handled properly
    return 'pt'
  }
  return locale.value
})

// FIXME: need to force Vue to re-render the range calendar because radix doesn't currently manage the updating of undated data and therefore doesn't allow clear
const rangeCalendarKey = ref(0)
const clearRange = () => {
  rangeCalendarKey.value++
}

const formatMonth = (date: DateValue) => {
  const parts = formatter.toParts(date, {
    month: 'long',
    year: 'numeric',
  })
  // Handling needed for ES and CAT - remove the 'de'/'del' litteral between month and year
  return parts.filter(item => item.type !== 'literal').map(item => item.value).join(' ')
}

defineExpose({ clearRange })
</script>

<template>
  <RangeCalendarRoot
    v-slot="{ weekDays, grid }"
    :key="`range-calendar${rangeCalendarKey}`"
    v-model="localValue"
    :number-of-months="numberOfMonths"
    :min-value="minValue"
    :max-value="maxValue"
    :is-date-unavailable="isDateUnavailable"
    :locale="localeFixed"
    :week-starts-on="weeksStartsOn"
    weekday-format="short"
  >
    <RangeCalendarHeader class="flex items-center justify-between">
      <RangeCalendarPrev
        class="inline-flex items-center cursor-pointer justify-center"
      >
        <YscIconsArrowLeft class="w-6 h-6" />
      </RangeCalendarPrev>
      <div class="flex justify-around grow capitalize text-lg">
        <RangeCalendarHeading>
          {{ formatMonth(grid[0].value) }}
        </RangeCalendarHeading>
        <RangeCalendarHeading v-if="numberOfMonths === 2">
          {{
            formatMonth(grid[grid.length - 1].value)
          }}
        </RangeCalendarHeading>
      </div>
      <RangeCalendarNext
        class="inline-flex items-center cursor-pointer justify-center"
      >
        <YscIconsArrowRight class="w-6 h-6" />
      </RangeCalendarNext>
    </RangeCalendarHeader>
    <div
      class="flex flex-col pt-4 sm:flex-row space-x-8"
    >
      <RangeCalendarGrid
        v-for="month in grid"
        :key="month.value.toString()"
        cellspacing="0"
        cellpadding="0"
        class="w-full space-y-1"
      >
        <RangeCalendarGridHead>
          <RangeCalendarGridRow class="mb-1 grid w-full grid-cols-7">
            <RangeCalendarHeadCell
              v-for="day in weekDays"
              :key="day"
              class="rounded-md text-sm font-light text-gray-500 capitalize"
            >
              {{ day }}
            </RangeCalendarHeadCell>
          </RangeCalendarGridRow>
        </RangeCalendarGridHead>
        <RangeCalendarGridBody class="grid space-y-1">
          <RangeCalendarGridRow
            v-for="(weekDates, index) in month.rows"
            :key="`weekDate-${index}`"
            class="grid grid-cols-7 text-center gap-x-1"
          >
            <RangeCalendarCell
              v-for="weekDate in weekDates"
              :key="weekDate.toString()"
              :date="weekDate"
              class="w-full"
            >
              <RangeCalendarCellTrigger
                :day="weekDate"
                :month="month.value"
                class="ysc-cell-trigger relative flex items-center justify-center text-sm w-full h-10 outline-none"
              />
            </RangeCalendarCell>
          </RangeCalendarGridRow>
        </RangeCalendarGridBody>
      </RangeCalendarGrid>
    </div>
  </RangeCalendarRoot>
</template>

<style scoped>
.ysc-cell-trigger[data-outside-month]{@apply hidden}.ysc-cell-trigger[data-disabled],.ysc-cell-trigger[data-unavailable]{@apply text-gray-300 pointer-events-none}.ysc-cell-trigger[data-selected]{@apply bg-pink-500 text-white font-semibold rounded}.ysc-cell-trigger[data-selected]:not([data-selection-start],.ysc-cell-trigger[data-selection-end]){@apply font-semibold bg-pink-50 text-pink-500}.ysc-cell-trigger[data-unavailable]{@apply line-through}.ysc-cell-trigger[data-value]:not([data-selection-start],.ysc-cell-trigger[data-selection-end]){@apply hover:border-2 hover:border-gray-900 hover:rounded focus:border-2 focus:border-gray-900 focus:rounded}
</style>
